import { useState } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { useFormState } from 'informed';
import { getAvailableUserPermissions } from 'lib/users';
import { User } from 'components/common';
import {
  Checkbox,
  Datepicker,
  FormGroup,
  Select,
  UserSelector
} from 'components/common/form';
import { addDays } from 'date-fns';

import type { AdminUser } from 'shared-types';

interface Props {
  disableAdmins?: boolean;
  disableSuperadmins?: boolean;
  existingAdminIds?: number[];
  onNewUsers?: (users: string[]) => void;
  targetUser?: AdminUser;
  validateEmails?: (emails: string[]) => string | undefined;
}

const AdminsAddOrEditForm = ({
  disableAdmins = false,
  disableSuperadmins = false,
  existingAdminIds = [],
  onNewUsers,
  targetUser,
  validateEmails
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, serverConfig } = useReduxState();

  const formState = useFormState();
  const [newUsers, setNewUsers] = useState<string[]>([]);

  const handleNewUsers = (users: string[]) => {
    setNewUsers(users);
    if (onNewUsers) onNewUsers(users);
  };

  const userPermissions = getAvailableUserPermissions({
    messages,
    serverConfig
  });

  return (
    <div className="form">
      {targetUser ? (
        <div className="user">
          <User enableProfileLink={true} user={targetUser} />
        </div>
      ) : (
        <UserSelector
          enforceEmail={true}
          existingUsers={existingAdminIds}
          onNewUsers={handleNewUsers}
          required={true}
          {...(validateEmails
            ? {
                validate: validateEmails,
                validateOnChange: true
              }
            : {})}
          {...{ keepState: true }}
        />
      )}

      {!targetUser &&
        serverConfig.accountAddForceExpirationDate &&
        newUsers.length > 0 && (
          <FormGroup
            htmlFor="userExpirationDate"
            label={messages.commonExpirationDateForNewUserAccounts}
            maxWidth="100%"
          >
            <Datepicker
              field="userExpirationDate"
              id="userExpirationDate"
              isClearable={false}
              keepState={true}
              maxDate={addDays(
                dateNow,
                serverConfig.accountAddForceExpirationDateMaxDays
              )}
              minDate={dateNow}
              required={true}
              validateOnChange={true}
              validateOnBlur={true}
              style={{ maxWidth: 200 }}
              placeholder={messages.commonExpirationDate}
            />
          </FormGroup>
        )}

      <FormGroup label={messages.settingsRole} maxWidth="200px">
        <Select
          field="role"
          id={`admins-add-or-edit-role-${targetUser?.id}`}
          options={[
            {
              isDisabled: disableSuperadmins,
              label: disableSuperadmins ? (
                <div data-tooltip={messages.usersAdminLicencesInUse}>
                  {messages.usersSuperadmin}
                </div>
              ) : (
                messages.usersSuperadmin
              ),
              value: 'superadmin'
            },
            {
              isDisabled: disableAdmins,
              label: disableAdmins ? (
                <div data-tooltip={messages.usersAdminLicencesInUse}>
                  {messages.usersAdmin}
                </div>
              ) : (
                messages.usersAdmin
              ),
              value: 'admin'
            }
          ]}
          {...{ keepState: true }}
        />
      </FormGroup>

      {formState.values?.role === 'admin' ? (
        <FormGroup label={messages.usersPermissions}>
          {Object.keys(userPermissions).map((permissionKey, i) => (
            <Checkbox
              key={permissionKey}
              field={`permissions['${permissionKey}']`}
              id={`permissions-${permissionKey}-${targetUser?.id}`}
              label={userPermissions[permissionKey]?.label}
              labelTooltip={userPermissions[permissionKey]?.helpText}
              stopPropagation={true}
              style={{ marginTop: i === 0 ? 0 : 5 }}
              {...{ keepState: true }}
            />
          ))}
        </FormGroup>
      ) : null}

      <style jsx>{`
        .permissions {
          padding-left: 25px;
        }
        .permission-option {
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};

export default AdminsAddOrEditForm;
